import { ref } from 'vue';
import {
  Query,
  GetDocLocation,
} from '@/generated/graphql.ts';
import { apolloService } from '@/apollo/apolloRequests.ts';

import { GET_LOCATION_DOCUMENT } from '@/components/TableTransactions/queriesTransactions.ts';
import { Notify } from 'quasar';
import { useRouter } from 'vue-router';

export default function useNavigateFolder() {
  const router = useRouter();
  const isPendingLocationDoc = ref<boolean>(false);
  const getLocationDocument = async (fileId: string) => {
    if (!fileId) return;
    isPendingLocationDoc.value = true;
    try {
      const data = (await apolloService.makeApolloRequest(
        'query',
        GET_LOCATION_DOCUMENT,
        {
          fileId,
        },
        undefined,
        false,
      )) as Query;
      if (data && data.accounting && data.accounting.getDocLocation) {
        return data.accounting.getDocLocation;
      } else {
        return null;
      }
    } catch (err) {
      Notify.create({
        message: 'Unable to retrieve the document location. It may have been deleted from the current accounting period.',
        color: 'warning',
        position: 'top',
      });
      throw err;
    } finally {
      isPendingLocationDoc.value = false;
    }
  };

  const handleNavigateToFolder = async (fileId: string) => {
    if (!fileId) return;
    const data = await getLocationDocument(fileId);
    if (!data) return;

    const routeParams = determineRouteParams(data, data.companyId, fileId);

    if (routeParams) {
      const url = router.resolve(routeParams).href;
      openInNewTab(url);
    }
  };

  const openInNewTab = (url: string) => {
    const newTab = window.open(url, '_blank');
    if (newTab) {
      newTab.focus();
    } else {
      Notify.create({
        message: 'Please allow popups for this website',
        color: 'negative',
        position: 'top',
      });
    }
  };

  const determineRouteParams = (data: GetDocLocation, companyId: string, fileId: string) => {
    if (data.companyId !== companyId) return null;
    if (data.contractsFolderId) {
      return {
        name: 'CompanyContractsFilesPage',
        params: {
          company_id: companyId,
          contracts_folder_id: data.contractsFolderId,
        },
      };
    }

    if (data.periodSubfolderId) {
      return {
        name: 'CompanySubFolderPeriodFilesPage',
        params: {
          company_id: companyId,
          folder_id: data.yearFolderId,
          period_folder_id: data.periodFolderId,
          subfolder_id: data.periodSubfolderId,
        },
        query: { file_id: fileId },
      };
    }

    return null;
  };

  return { isPendingLocationDoc, handleNavigateToFolder };
}
